import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  Building2, Clock, CheckCircle, CreditCard, AlertCircle, LogOut,
  User, Phone, Mail, MapPin, FileText,
  IndianRupee, Calendar, ArrowRight,
  Loader2, BadgeCheck, XCircle, Edit, Save, X,
  Users, TrendingUp, GraduationCap, Briefcase, BarChart3
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Status configuration
const STATUS_CONFIG = {
  pending: {
    label: "Under Review",
    color: "amber",
    bgColor: "bg-amber-500/10",
    borderColor: "border-amber-500/30",
    textColor: "text-amber-400",
    icon: Clock,
    step: 1
  },
  stage1_approved: {
    label: "Stage 1 Approved",
    color: "blue",
    bgColor: "bg-blue-500/10",
    borderColor: "border-blue-500/30",
    textColor: "text-blue-400",
    icon: CheckCircle,
    step: 2
  },
  payment_pending: {
    label: "Payment Pending",
    color: "purple",
    bgColor: "bg-purple-500/10",
    borderColor: "border-purple-500/30",
    textColor: "text-purple-400",
    icon: CreditCard,
    step: 2
  },
  approved: {
    label: "Active",
    color: "emerald",
    bgColor: "bg-emerald-500/10",
    borderColor: "border-emerald-500/30",
    textColor: "text-emerald-400",
    icon: BadgeCheck,
    step: 3
  },
  active: {
    label: "Active",
    color: "emerald",
    bgColor: "bg-emerald-500/10",
    borderColor: "border-emerald-500/30",
    textColor: "text-emerald-400",
    icon: BadgeCheck,
    step: 3
  },
  rejected: {
    label: "Rejected",
    color: "red",
    bgColor: "bg-red-500/10",
    borderColor: "border-red-500/30",
    textColor: "text-red-400",
    icon: XCircle,
    step: 0
  }
};

const PARTNER_FEES = {
  TC: { name: "Training Center", fee: 50000 },
  DLC: { name: "District Partner", fee: 150000 },
  SP: { name: "State Partner", fee: 500000 }
};

// Axios instance with auth
const centerApi = axios.create({ baseURL: API });
centerApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('center_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ========================================
// PROGRESS BAR COMPONENT
// ========================================
const ProgressBar = ({ currentStep, status }) => {
  const steps = [
    { step: 1, label: "Application Submitted", icon: FileText },
    { step: 2, label: "Stage 1 Approved", icon: CheckCircle },
    { step: 3, label: "Payment & Activation", icon: BadgeCheck }
  ];

  return (
    <div className="w-full mb-8">
      <div className="flex items-center justify-between relative">
        {/* Progress line */}
        <div className="absolute top-5 left-0 right-0 h-1 bg-slate-700 z-0">
          <div 
            className="h-full bg-gradient-to-r from-emerald-500 to-blue-500 transition-all duration-500"
            style={{ width: `${((currentStep - 1) / 2) * 100}%` }}
          />
        </div>

        {steps.map((s, idx) => {
          const Icon = s.icon;
          const isCompleted = currentStep > s.step;
          const isCurrent = currentStep === s.step;
          
          return (
            <div key={idx} className="flex flex-col items-center z-10">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                isCompleted ? 'bg-emerald-500' :
                isCurrent ? 'bg-blue-500 ring-4 ring-blue-500/30' :
                'bg-slate-700'
              }`}>
                {isCompleted ? (
                  <CheckCircle className="w-5 h-5 text-white" />
                ) : (
                  <Icon className={`w-5 h-5 ${isCurrent ? 'text-white' : 'text-slate-500'}`} />
                )}
              </div>
              <p className={`text-xs mt-2 font-medium text-center ${
                isCompleted ? 'text-emerald-400' :
                isCurrent ? 'text-blue-400' :
                'text-slate-500'
              }`}>
                {s.label}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ========================================
// STAGE 1: PENDING DASHBOARD
// ========================================
const PendingDashboard = ({ application, centerUser }) => {
  return (
    <div className="max-w-3xl mx-auto">
      {/* Status Card */}
      <div className="bg-amber-500/10 border border-amber-500/30 rounded-2xl p-8 text-center mb-8">
        <div className="w-20 h-20 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
          <Clock className="w-10 h-10 text-amber-400 animate-pulse" />
        </div>
        <h2 className="text-2xl font-bold text-amber-400 mb-2">Application Under Review</h2>
        <p className="text-slate-400">
          Our team is reviewing your application. This usually takes 24-48 hours.
        </p>
        {application?.id && (
          <p className="text-slate-500 text-sm mt-4 font-mono">
            Application ID: {application.id.slice(0, 8)}...
          </p>
        )}
      </div>

      {/* Submitted Details (Read-Only) */}
      <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 border border-slate-700">
        <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
          <FileText className="w-5 h-5 text-blue-400" />
          Submitted Application Details
        </h3>

        <div className="grid sm:grid-cols-2 gap-6">
          <div className="space-y-4">
            <InfoItem icon={<User />} label="Owner Name" value={application?.owner_name} />
            <InfoItem icon={<Phone />} label="Mobile" value={application?.mobile} />
            <InfoItem icon={<Mail />} label="Email" value={application?.email} />
          </div>
          <div className="space-y-4">
            <InfoItem icon={<Building2 />} label="Center Name" value={application?.center_name} />
            <InfoItem icon={<MapPin />} label="Location" value={`${application?.city}, ${application?.district}, ${application?.state}`} />
            <InfoItem icon={<IndianRupee />} label="Partner Type" value={PARTNER_FEES[application?.partner_type]?.name || 'Training Center'} />
          </div>
        </div>

        <div className="mt-6 p-4 bg-slate-700/50 rounded-xl">
          <p className="text-slate-400 text-sm">
            <AlertCircle className="w-4 h-4 inline mr-2 text-amber-400" />
            You cannot edit your details or access other features until your application is approved.
          </p>
        </div>
      </div>

      {/* What's Next */}
      <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 border border-slate-700 mt-6">
        <h3 className="text-lg font-bold text-white mb-4">What happens next?</h3>
        <ul className="space-y-3 text-slate-300 text-sm">
          <li className="flex items-start gap-3">
            <span className="w-6 h-6 bg-blue-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">1</span>
            <span>Our team reviews your application details</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-6 h-6 bg-blue-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">2</span>
            <span>Upon approval, you'll be notified via email/SMS</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-6 h-6 bg-blue-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">3</span>
            <span>Complete your profile and pay the affiliation fee</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-6 h-6 bg-blue-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">4</span>
            <span>Get your Center Code and start operations!</span>
          </li>
        </ul>
      </div>
    </div>
  );
};

// ========================================
// STAGE 2: APPROVED (PRE-AFFILIATION)
// ========================================
const Stage1ApprovedDashboard = ({ application, centerUser, onRefresh }) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editForm, setEditForm] = useState({
    owner_name: application?.owner_name || '',
    center_name: application?.center_name || '',
    mobile: application?.mobile || '',
    address: application?.address || '',
    city: application?.city || '',
    district: application?.district || '',
    state: application?.state || '',
    pin_code: application?.pin_code || ''
  });

  const fee = PARTNER_FEES[application?.partner_type]?.fee || 50000;

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await centerApi.put(`/center/profile`, editForm);
      toast.success('Profile updated successfully');
      setEditing(false);
      onRefresh();
    } catch (error) {
      toast.error('Failed to update profile');
    }
    setSaving(false);
  };

  const handlePayment = async () => {
    try {
      const response = await centerApi.post(`/payment/create-partner-order`, {
        application_id: application.id,
        amount: fee
      });

      const options = {
        key: response.data.razorpay_key_id,
        amount: response.data.amount,
        currency: "INR",
        name: "Iqra Skills",
        description: `${PARTNER_FEES[application?.partner_type]?.name || 'Partner'} Affiliation Fee`,
        order_id: response.data.order_id,
        handler: async function (razorpayResponse) {
          try {
            await centerApi.post(`/payment/verify-partner-payment`, {
              razorpay_order_id: razorpayResponse.razorpay_order_id,
              razorpay_payment_id: razorpayResponse.razorpay_payment_id,
              razorpay_signature: razorpayResponse.razorpay_signature,
              application_id: application.id
            });
            toast.success('Payment successful! Your center is being activated.');
            onRefresh();
          } catch (err) {
            toast.error('Payment verification failed');
          }
        },
        prefill: {
          name: application?.owner_name,
          email: application?.email,
          contact: application?.mobile
        },
        theme: { color: "#2563eb" }
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
    }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-8">
      {/* Main Content */}
      <div className="lg:col-span-2 space-y-6">
        {/* Status Banner */}
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-2xl p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-blue-500/20 rounded-xl flex items-center justify-center">
                <CheckCircle className="w-7 h-7 text-blue-400" />
              </div>
              <div>
                <p className="text-slate-400 text-sm">Application Status</p>
                <h2 className="text-2xl font-bold text-blue-400">Stage 1 Approved!</h2>
                {application?.temp_id && (
                  <p className="text-slate-500 text-sm font-mono mt-1">ID: {application.temp_id}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Profile Review Section */}
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 border border-slate-700">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <User className="w-5 h-5 text-purple-400" />
              Profile Details
            </h3>
            {!editing ? (
              <Button onClick={() => setEditing(true)} variant="outline" size="sm" className="text-blue-400 border-blue-500/30">
                <Edit className="w-4 h-4 mr-2" /> Edit
              </Button>
            ) : (
              <div className="flex gap-2">
                <Button onClick={() => setEditing(false)} variant="ghost" size="sm" className="text-slate-400">
                  <X className="w-4 h-4 mr-1" /> Cancel
                </Button>
                <Button onClick={handleSaveProfile} disabled={saving} size="sm" className="bg-emerald-600 hover:bg-emerald-700">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save
                </Button>
              </div>
            )}
          </div>

          {editing ? (
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-400">Owner Name</Label>
                <Input value={editForm.owner_name} onChange={(e) => setEditForm({...editForm, owner_name: e.target.value})} className="mt-1 bg-slate-700/50 border-slate-600" />
              </div>
              <div>
                <Label className="text-slate-400">Center Name</Label>
                <Input value={editForm.center_name} onChange={(e) => setEditForm({...editForm, center_name: e.target.value})} className="mt-1 bg-slate-700/50 border-slate-600" />
              </div>
              <div>
                <Label className="text-slate-400">Mobile</Label>
                <Input value={editForm.mobile} onChange={(e) => setEditForm({...editForm, mobile: e.target.value})} className="mt-1 bg-slate-700/50 border-slate-600" />
              </div>
              <div>
                <Label className="text-slate-400">City</Label>
                <Input value={editForm.city} onChange={(e) => setEditForm({...editForm, city: e.target.value})} className="mt-1 bg-slate-700/50 border-slate-600" />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-slate-400">Address</Label>
                <Textarea value={editForm.address} onChange={(e) => setEditForm({...editForm, address: e.target.value})} className="mt-1 bg-slate-700/50 border-slate-600" rows={2} />
              </div>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-6">
              <div className="space-y-4">
                <InfoItem icon={<User />} label="Owner Name" value={application?.owner_name} />
                <InfoItem icon={<Phone />} label="Mobile" value={application?.mobile} />
                <InfoItem icon={<Mail />} label="Email" value={application?.email} />
              </div>
              <div className="space-y-4">
                <InfoItem icon={<Building2 />} label="Center Name" value={application?.center_name} />
                <InfoItem icon={<MapPin />} label="Location" value={`${application?.city}, ${application?.district}, ${application?.state}`} />
                <InfoItem icon={<IndianRupee />} label="Partner Type" value={PARTNER_FEES[application?.partner_type]?.name} />
              </div>
            </div>
          )}
        </div>

        {/* Instructions Panel */}
        <div className="bg-gradient-to-br from-blue-500/10 to-purple-500/10 border border-blue-500/20 rounded-2xl p-6">
          <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-blue-400" />
            Complete These Steps
          </h3>
          <ul className="space-y-3">
            <li className="flex items-center gap-3 text-slate-300">
              <CheckCircle className="w-5 h-5 text-emerald-400" />
              <span>Review and update your profile details</span>
            </li>
            <li className="flex items-center gap-3 text-blue-400 font-medium">
              <ArrowRight className="w-5 h-5" />
              <span>Pay affiliation fee to activate your center</span>
            </li>
          </ul>
        </div>

        {/* Restricted Access Notice */}
        <div className="bg-slate-800/50 border border-red-500/20 rounded-2xl p-6">
          <h3 className="text-red-400 font-bold mb-3 flex items-center gap-2">
            <XCircle className="w-5 h-5" />
            Restricted Until Payment
          </h3>
          <div className="grid sm:grid-cols-3 gap-3">
            {['Student Management', 'Course Access', 'Full Dashboard', 'Fee Collection', 'Attendance', 'Reports'].map((item) => (
              <div key={item} className="flex items-center gap-2 text-slate-500 text-sm">
                <XCircle className="w-4 h-4 text-red-400/50" />
                {item}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Sidebar */}
      <div className="space-y-6">
        {/* Payment Card */}
        <div className="bg-gradient-to-br from-emerald-600 to-blue-600 rounded-2xl p-6 text-white">
          <div className="flex items-center gap-3 mb-4">
            <CreditCard className="w-6 h-6" />
            <h3 className="font-bold text-lg">Affiliation Fee</h3>
          </div>

          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-emerald-100">Partner Type</span>
              <span className="font-medium">{PARTNER_FEES[application?.partner_type]?.name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-emerald-100">Fee Amount</span>
              <span className="font-bold text-2xl">₹{fee.toLocaleString()}</span>
            </div>
          </div>

          <Button 
            onClick={handlePayment}
            className="w-full mt-6 bg-white text-emerald-700 hover:bg-emerald-50 font-bold py-6"
          >
            <CreditCard className="w-5 h-5 mr-2" />
            Pay Now & Activate
          </Button>
        </div>

        {/* Help Card */}
        <HelpCard />
      </div>
    </div>
  );
};

// ========================================
// STAGE 3: ACTIVE DASHBOARD
// ========================================
const ActiveDashboard = ({ center, centerUser, application }) => {
  const navigate = useNavigate();
  
  return (
    <div className="space-y-8">
      {/* Center Code Banner */}
      <div className="bg-gradient-to-r from-emerald-600 via-blue-600 to-purple-600 rounded-2xl p-6 text-white">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
              <BadgeCheck className="w-8 h-8" />
            </div>
            <div>
              <p className="text-emerald-100 text-sm">Your Center Code</p>
              <h2 className="text-3xl font-bold font-mono">{center?.center_code || application?.center_code || 'IQRA-XX-00-000'}</h2>
            </div>
          </div>
          <div className="text-center sm:text-right">
            <span className="inline-flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full">
              <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
              Center Active
            </span>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={<Users />} label="Total Students" value="0" color="blue" />
        <StatCard icon={<IndianRupee />} label="Revenue" value="₹0" color="emerald" />
        <StatCard icon={<GraduationCap />} label="Courses" value="12" color="purple" />
        <StatCard icon={<TrendingUp />} label="This Month" value="₹0" color="amber" />
      </div>

      {/* Commission Structure Info */}
      <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 border border-slate-700">
        <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
          <IndianRupee className="w-5 h-5 text-emerald-400" />
          Commission Structure
        </h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-emerald-400 font-medium">Diploma & Degree Programs</span>
              <span className="bg-emerald-500/20 text-emerald-400 px-2 py-1 rounded text-xs font-bold">40% Your Share</span>
            </div>
            <p className="text-slate-400 text-sm">PGDCA, BCA, BBA, B.Com, MBA, MCA, etc.</p>
            <p className="text-slate-500 text-xs mt-1">Iqra Skills: 60% | Your Center: 40%</p>
          </div>
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-blue-400 font-medium">Professional Courses</span>
              <span className="bg-blue-500/20 text-blue-400 px-2 py-1 rounded text-xs font-bold">70% Your Share</span>
            </div>
            <p className="text-slate-400 text-sm">AutoCAD, Tally, Digital Marketing, etc.</p>
            <p className="text-slate-500 text-xs mt-1">Iqra Skills: 30% | Your Center: 70%</p>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 border border-slate-700">
        <h3 className="text-lg font-bold text-white mb-6">Quick Actions</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <QuickActionCard 
            icon={<Users />}
            title="Student Management"
            desc="Add & manage students"
            onClick={() => navigate('/center/dashboard')}
            color="blue"
          />
          <QuickActionCard 
            icon={<CreditCard />}
            title="Fee Collection"
            desc="Record student fees"
            onClick={() => navigate('/center/dashboard')}
            color="emerald"
          />
          <QuickActionCard 
            icon={<Calendar />}
            title="Attendance"
            desc="Mark attendance"
            onClick={() => navigate('/center/dashboard')}
            color="purple"
          />
          <QuickActionCard 
            icon={<Briefcase />}
            title="CRM"
            desc="Manage leads"
            onClick={() => navigate('/center/dashboard')}
            color="pink"
          />
          <QuickActionCard 
            icon={<BarChart3 />}
            title="Reports"
            desc="View analytics"
            onClick={() => navigate('/center/dashboard')}
            color="amber"
          />
          <QuickActionCard 
            icon={<GraduationCap />}
            title="Courses"
            desc="Course catalog"
            onClick={() => navigate('/center/dashboard')}
            color="cyan"
          />
        </div>
      </div>

      {/* Full Dashboard Access */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white text-center">
        <h3 className="text-2xl font-bold mb-4">Access Full Dashboard</h3>
        <p className="text-blue-100 mb-6">
          Your center is now active! Access the complete dashboard with all features.
        </p>
        <Button 
          onClick={() => navigate('/center/dashboard')}
          className="bg-white text-blue-700 hover:bg-blue-50 font-bold px-8 py-6 text-lg"
        >
          Go to Full Dashboard <ArrowRight className="w-5 h-5 ml-2" />
        </Button>
      </div>
    </div>
  );
};

// ========================================
// HELPER COMPONENTS
// ========================================
const InfoItem = ({ icon, label, value }) => (
  <div className="flex items-center gap-3">
    <div className="w-10 h-10 bg-slate-700/50 rounded-lg flex items-center justify-center text-slate-400">
      {React.cloneElement(icon, { className: "w-5 h-5" })}
    </div>
    <div>
      <p className="text-slate-500 text-sm">{label}</p>
      <p className="text-white font-medium">{value || '-'}</p>
    </div>
  </div>
);

const StatCard = ({ icon, label, value, color }) => (
  <div className={`bg-${color}-500/10 border border-${color}-500/20 rounded-xl p-4`}>
    <div className="flex items-center gap-3">
      <div className={`w-10 h-10 bg-${color}-500/20 rounded-lg flex items-center justify-center text-${color}-400`}>
        {React.cloneElement(icon, { className: "w-5 h-5" })}
      </div>
      <div>
        <p className="text-slate-500 text-sm">{label}</p>
        <p className="text-xl font-bold text-white">{value}</p>
      </div>
    </div>
  </div>
);

const QuickActionCard = ({ icon, title, desc, onClick, color }) => (
  <button
    onClick={onClick}
    className={`bg-${color}-500/10 hover:bg-${color}-500/20 border border-${color}-500/20 rounded-xl p-4 text-left transition-all group`}
  >
    <div className={`w-10 h-10 bg-${color}-500/20 rounded-lg flex items-center justify-center text-${color}-400 mb-3 group-hover:scale-110 transition-transform`}>
      {React.cloneElement(icon, { className: "w-5 h-5" })}
    </div>
    <h4 className="text-white font-semibold">{title}</h4>
    <p className="text-slate-500 text-sm">{desc}</p>
  </button>
);

const HelpCard = () => (
  <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 border border-slate-700">
    <h3 className="font-bold text-white mb-4">Need Help?</h3>
    <div className="space-y-3">
      <a href="tel:+919521484003" className="flex items-center gap-3 p-3 bg-slate-700/50 rounded-xl hover:bg-slate-700 transition-colors">
        <Phone className="w-5 h-5 text-green-400" />
        <div>
          <p className="text-white font-medium">Call Support</p>
          <p className="text-slate-400 text-sm">+91 95214 84003</p>
        </div>
      </a>
      <a href="https://wa.me/919521484003" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-3 bg-slate-700/50 rounded-xl hover:bg-slate-700 transition-colors">
        <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
          <span className="text-white text-xs font-bold">W</span>
        </div>
        <div>
          <p className="text-white font-medium">WhatsApp</p>
          <p className="text-slate-400 text-sm">Quick Response</p>
        </div>
      </a>
    </div>
  </div>
);

// ========================================
// MAIN COMPONENT
// ========================================
const StageBasedCenterDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [centerUser, setCenterUser] = useState(null);
  const [application, setApplication] = useState(null);
  const [center, setCenter] = useState(null);
  const [status, setStatus] = useState('pending');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const token = localStorage.getItem('center_token');
    if (!token) {
      navigate('/center/login');
      return;
    }

    try {
      // Get center user info
      const userResponse = await centerApi.get('/center/me');
      const userData = userResponse.data;
      setCenterUser(userData);

      // Determine status
      const userStatus = userData.status || 'pending';
      setStatus(userStatus);

      // Get application details if available
      if (userData.application_id) {
        try {
          const trackResponse = await axios.get(`${API}/partner/track/${userData.application_id}`);
          setApplication(trackResponse.data.application);
          
          // Update status from application
          if (trackResponse.data.application?.status) {
            setStatus(trackResponse.data.application.status);
          }
        } catch (err) {
          console.log('Could not fetch application:', err);
        }
      }

      // If active, get center details
      if (userStatus === 'active' || userData.center_id) {
        try {
          const centerResponse = await centerApi.get('/center/profile');
          setCenter(centerResponse.data);
        } catch (err) {
          console.log('Could not fetch center:', err);
        }
      }

    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('center_token');
        navigate('/center/login');
      }
    }
    setLoading(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('center_token');
    localStorage.removeItem('center_info');
    navigate('/auth/login');
    toast.success('Logged out successfully');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  const statusConfig = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  const currentStep = statusConfig.step;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="bg-slate-800/50 backdrop-blur-sm border-b border-slate-700 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_2f370b5d-868a-4240-92b5-92a3302f8c5a/artifacts/4n9x2udq_1%20copy.png" 
                alt="Iqra Skills Logo" 
                className="w-10 h-10 rounded-full object-cover"
              />
              <div>
                <h1 className="text-white font-bold">{centerUser?.center_name || application?.center_name || 'Partner Dashboard'}</h1>
                <p className="text-slate-400 text-sm flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${statusConfig.bgColor.replace('/10', '')}`} />
                  {statusConfig.label}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              {(center?.center_code || application?.center_code) && (
                <div className="hidden sm:block bg-slate-700/50 px-4 py-2 rounded-lg">
                  <p className="text-slate-400 text-xs">Center Code</p>
                  <p className="text-white font-mono font-bold">{center?.center_code || application?.center_code}</p>
                </div>
              )}
              <div className="text-right hidden sm:block">
                <p className="text-white font-medium">{centerUser?.owner_name || application?.owner_name}</p>
                <p className="text-slate-400 text-sm">{centerUser?.email || application?.email}</p>
              </div>
              <Button 
                onClick={handleLogout}
                variant="ghost"
                className="text-slate-400 hover:text-white hover:bg-slate-700"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Progress Bar */}
        <ProgressBar currentStep={currentStep} status={status} />

        {/* Stage-Based Content */}
        {status === 'pending' && (
          <PendingDashboard application={application} centerUser={centerUser} />
        )}

        {(status === 'stage1_approved' || status === 'payment_pending') && (
          <Stage1ApprovedDashboard 
            application={application} 
            centerUser={centerUser} 
            onRefresh={fetchData}
          />
        )}

        {(status === 'approved' || status === 'active') && (
          <ActiveDashboard 
            center={center} 
            centerUser={centerUser} 
            application={application}
          />
        )}

        {status === 'rejected' && (
          <div className="max-w-2xl mx-auto text-center">
            <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-8">
              <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-red-400 mb-2">Application Rejected</h2>
              <p className="text-slate-400 mb-4">
                Unfortunately, your application has been rejected. 
                {application?.rejection_reason && (
                  <span className="block mt-2 text-red-300">Reason: {application.rejection_reason}</span>
                )}
              </p>
              <Button onClick={() => navigate('/partner-apply')} className="bg-blue-600 hover:bg-blue-700">
                Apply Again
              </Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default StageBasedCenterDashboard;
